import React, { useState, useEffect } from 'react';
import { getSocket } from '../utils/socket';
import { getBranchTheme, getUserTheme } from '../utils/auth';
import { applyTheme, resolveTheme } from '../theme';
import './AdminGameDisplay.css';
import logo from '../assets/ctta-logo.avif';

function AdminGameDisplay({ session }) {
  const [socket, setSocket] = useState(null);
  const [displayState, setDisplayState] = useState('welcome');
  const [participantCount, setParticipantCount] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [questionResult, setQuestionResult] = useState(null);
  const [finalLeaderboard, setFinalLeaderboard] = useState([]);
  const [countdown, setCountdown] = useState(0);
  const [countdownQuestionNumber, setCountdownQuestionNumber] = useState(null);
  const [answerCount, setAnswerCount] = useState(0);
  const [participants, setParticipants] = useState([]);
  const [namePage, setNamePage] = useState(0);

  const PAGE_SIZE = 24;

  // Apply session/user/branch theme on mount
  useEffect(() => {
    const sessionTheme = session.theme || null;
    applyTheme(resolveTheme(getBranchTheme(), getUserTheme(), sessionTheme));
  }, [session]);

  useEffect(() => {
    const socketInstance = getSocket();
    setSocket(socketInstance);

    // Join as display
    socketInstance.emit('display_join', {
      sessionCode: session.code
    });

    // Listen for display events
    socketInstance.on('display_joined', (data) => {
      console.log('Display joined:', data);
      setDisplayState(data.currentState);
      setParticipantCount(data.participantCount);
      if (Array.isArray(data.participants)) {
        setParticipants(data.participants);
      }

      // Handle state-specific data when display connects mid-game
      if (data.currentQuestion) {
        // Display connecting during active question
        setCurrentQuestion(data.currentQuestion);
        setTimeLeft(data.currentQuestion.timeRemaining || data.currentQuestion.timeLimit);
      } else if (data.questionResult) {
        // Display connecting during results display
        setQuestionResult(data.questionResult);
      } else if (data.finalLeaderboard) {
        // Display connecting during final leaderboard
        setFinalLeaderboard(data.finalLeaderboard);
      }
    });

    socketInstance.on('participant_joined', (data) => {
      setParticipantCount(data.participantCount);
      if (data.name) {
        setParticipants(prev => prev.includes(data.name) ? prev : [...prev, data.name]);
      }
    });

    socketInstance.on('show_welcome', (data) => {
      setDisplayState('welcome');
      setParticipantCount(data.participantCount);
    });

    socketInstance.on('question_countdown', (data) => {
      setDisplayState('countdown');
      setCountdown(data.seconds);
      setCountdownQuestionNumber(data.questionNumber);
    });

    socketInstance.on('question_started', (data) => {
      setDisplayState('question');
      setCurrentQuestion(data);
      setTimeLeft(data.timeLimit);
      setCountdown(0);
      setAnswerCount(0);
    });

    socketInstance.on('new_response', (data) => {
      setAnswerCount(data.responseCount);
      setParticipantCount(data.totalParticipants);
    });

    socketInstance.on('question_ended', (data) => {
      setDisplayState('results');
      setQuestionResult(data.result);
    });

    socketInstance.on('show_leaderboard', (data) => {
      setDisplayState('finished');
      setFinalLeaderboard(data.leaderboard);
    });

    return () => {
      socketInstance.off('display_joined');
      socketInstance.off('participant_joined');
      socketInstance.off('show_welcome');
      socketInstance.off('question_countdown');
      socketInstance.off('question_started');
      socketInstance.off('question_ended');
      socketInstance.off('show_leaderboard');
      socketInstance.off('new_response');
    };
  }, [session]);

  // Timer countdown for display
  useEffect(() => {
    if (displayState === 'question' && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [displayState, timeLeft]);

  // Countdown ticker
  useEffect(() => {
    if (displayState === 'countdown' && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [displayState, countdown]);

  // Auto-rotate player name pages every 3s when on welcome screen
  useEffect(() => {
    if (displayState !== 'welcome' || participants.length <= PAGE_SIZE) return;
    const totalPages = Math.ceil(participants.length / PAGE_SIZE);
    const id = setInterval(() => {
      setNamePage(p => (p + 1) % totalPages);
    }, 3000);
    return () => clearInterval(id);
  }, [displayState, participants.length]);

  // ── Shared sidebar (shown on every screen) ──
  const sidebar = (
    <div className="ds-sidebar">
      <div className="ds-brand">
        <img src={logo} alt="CTTA Logo" className="ds-brand-logo" />
        <div className="ds-brand-text">
          <span className="ds-brand-name">Central Texas Tamil Academy</span>
        </div>
      </div>
      <div className="ds-divider" />
      <div className="ds-code">{session.code}</div>
      {session.qrCode && (
        <img src={session.qrCode} alt="Join QR Code" className="ds-qr" />
      )}
      <p className="ds-url">{session.joinUrl}</p>
      <div className="ds-count">
        <span className="ds-count-num">{participantCount}</span>
        <span className="ds-count-label">👥 Players Joined</span>
      </div>
    </div>
  );

  // Welcome Screen
  if (displayState === 'welcome') {
    const totalPages = Math.ceil(participants.length / PAGE_SIZE);
    const pageNames = participants.slice(namePage * PAGE_SIZE, (namePage + 1) * PAGE_SIZE);

    return (
      <div className="display-with-sidebar welcome-display">
        {sidebar}
        <div className="ds-main">
          <h1 className="display-title">Welcome to {session.gameName || 'CTTA Trivia'}!</h1>

          <div className="welcome-names-col">
            {participants.length === 0 ? (
              <p className="welcome-names-empty">Players will appear here as they join...</p>
            ) : (
              <>
                <div className="welcome-names-grid">
                  {pageNames.map((name) => (
                    <div key={name} className="welcome-name-chip">{name}</div>
                  ))}
                </div>
                {totalPages > 1 && (
                  <div className="welcome-page-dots">
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <span key={i} className={`welcome-dot ${i === namePage ? 'active' : ''}`} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="waiting-message">
            <div className="pulse-dot"></div>
            <span>Waiting for game to start...</span>
          </div>
        </div>
      </div>
    );
  }

  // Countdown Screen
  if (displayState === 'countdown') {
    return (
      <div className="display-with-sidebar countdown-display">
        {sidebar}
        <div className="ds-main ds-main-center">
          <h2 className="countdown-label">Question {countdownQuestionNumber} is coming up...</h2>
          <div className="countdown-number">{countdown}</div>
          <p className="countdown-sub">Get ready!</p>
        </div>
      </div>
    );
  }

  // Question Display
  if (displayState === 'question' && currentQuestion) {
    return (
      <div className="display-with-sidebar question-display">
        {sidebar}
        <div className="ds-main">
          <div className="display-results-split">

            {/* Left: question + options */}
            <div className="display-chart-panel">
              <div className="question-header-display">
                <div className="question-number">Question {currentQuestion.questionNumber}</div>
                <div className="timer-display">
                  <span className="timer-icon-display">⏱</span>
                  <span className="timer-value-display">{timeLeft}s</span>
                </div>
              </div>
              <h2 className="question-text-display">{currentQuestion.text}</h2>
              {currentQuestion.type === 'multiple_choice' && (
                <div className="options-display">
                  {currentQuestion.options.map((option, index) => (
                    <div key={index} className="option-display">
                      <span className="option-letter">{String.fromCharCode(65 + index)}</span>
                      <span className="option-text">{option}</span>
                    </div>
                  ))}
                </div>
              )}
              {currentQuestion.type === 'free_text' && (
                <div className="text-question-indicator">
                  <p>Open-ended question - Type your answer on your device</p>
                </div>
              )}
            </div>

            {/* Right: live stats */}
            <div className="display-scoreboard-panel">
              <h2 className="panel-title">Live Stats</h2>
              <div className="live-stats">
                <div className="live-stat-card">
                  <div className="live-stat-icon">👥</div>
                  <div className="live-stat-number">{participantCount}</div>
                  <div className="live-stat-label">Participants</div>
                </div>
                <div className="live-stat-card">
                  <div className="live-stat-icon">✅</div>
                  <div className="live-stat-number">{answerCount}</div>
                  <div className="live-stat-label">Answered</div>
                </div>
                <div className="live-stat-progress">
                  <div
                    className="live-stat-bar"
                    style={{ width: participantCount > 0 ? `${Math.round((answerCount / participantCount) * 100)}%` : '0%' }}
                  />
                  <span className="live-stat-pct">
                    {participantCount > 0 ? Math.round((answerCount / participantCount) * 100) : 0}%
                  </span>
                </div>
              </div>
              <div className="answer-prompt" style={{ marginTop: 'auto' }}>
                Answer on your device now!
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  }

  // Results Display
  if (displayState === 'results' && questionResult) {

    // Multiple choice
    if (questionResult.type === 'multiple_choice' && Array.isArray(questionResult.options)) {
      const total = questionResult.responses ? questionResult.responses.length : 0;
      const counts = questionResult.answerCounts || [];
      const correct = questionResult.correctAnswer;
      const top5 = (questionResult.cumulativeLeaderboard || []).slice(0, 5);

      return (
        <div className="display-with-sidebar results-display">
          {sidebar}
          <div className="ds-main">
            <div className="display-results-split">

              <div className="display-chart-panel">
                <p className="display-question-text">
                  {questionResult.questionText || (currentQuestion && currentQuestion.text)}
                </p>
                <div className="display-answer-chart">
                  {questionResult.options.map((option, index) => {
                    const count = counts[index] || 0;
                    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                    const isCorrect = index === correct;
                    return (
                      <div key={index} className={`display-chart-row ${isCorrect ? 'display-correct-row' : ''}`}>
                        <div className="display-chart-label">
                          <span className="display-option-letter">{String.fromCharCode(65 + index)}</span>
                          <span className="display-option-text">{option}</span>
                          {isCorrect && <span className="display-correct-mark">✓</span>}
                        </div>
                        <div className="display-chart-bar-wrap">
                          <div
                            className={`display-chart-bar ${isCorrect ? 'display-bar-correct' : 'display-bar-wrong'}`}
                            style={{ width: `${pct}%` }}
                          />
                          <span className="display-chart-count">{count} ({pct}%)</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="display-total-responses">{total} response{total !== 1 ? 's' : ''}</p>
              </div>

              <div className="display-scoreboard-panel">
                <h2 className="panel-title">🏆 Leaderboard</h2>
                <div className="display-top5">
                  {top5.map((entry, index) => (
                    <div key={index} className={`display-top5-entry rank-display-${index + 1}`}>
                      <span className="display-top5-rank">{index + 1}</span>
                      <span className="display-top5-name">{entry.name}</span>
                      <span className="display-top5-pts">{entry.totalScore} pts</span>
                    </div>
                  ))}
                  {top5.length === 0 && <p className="no-scores">No scores yet</p>}
                </div>
              </div>

            </div>
          </div>
        </div>
      );
    }

    // Free text
    if (questionResult.type === 'free_text') {
      if (questionResult.wordCloudData && questionResult.wordCloudData.length > 0) {
        const maxValue = Math.max(...questionResult.wordCloudData.map(w => w.value));
        const top5Words = questionResult.wordCloudData.slice(0, 5);
        return (
          <div className="display-with-sidebar results-display">
            {sidebar}
            <div className="ds-main">
              <div className="display-results-split">

                <div className="display-chart-panel">
                  <p className="display-question-text">
                    {questionResult.questionText || (currentQuestion && currentQuestion.text)}
                  </p>
                  <div className="word-cloud-container">
                    {questionResult.wordCloudData.map((word, index) => {
                      const fontSize = 20 + (word.value / maxValue) * 80;
                      const colors = ['#667eea', '#764ba2', '#f093fb', '#4ECDC4', '#45B7D1', '#FF6B6B'];
                      return (
                        <span
                          key={index}
                          className="word-cloud-word"
                          style={{ fontSize: `${fontSize}px`, color: colors[index % colors.length], margin: '8px 12px', display: 'inline-block', fontWeight: 'bold' }}
                        >
                          {word.text}
                        </span>
                      );
                    })}
                  </div>
                  <p className="display-total-responses">{questionResult.responses.length} response{questionResult.responses.length !== 1 ? 's' : ''}</p>
                </div>

                <div className="display-scoreboard-panel">
                  <h2 className="panel-title">Most Mentioned</h2>
                  <div className="display-top5">
                    {top5Words.map((word, index) => (
                      <div key={index} className={`display-top5-entry rank-display-${index + 1}`}>
                        <span className="display-top5-rank">{index + 1}</span>
                        <span className="display-top5-name">{word.text}</span>
                        <span className="display-top5-pts">{word.value}×</span>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </div>
          </div>
        );
      }

      if (questionResult.responses) {
        return (
          <div className="display-with-sidebar results-display">
            {sidebar}
            <div className="ds-main">
              <p className="display-question-text">
                {questionResult.questionText || (currentQuestion && currentQuestion.text)}
              </p>
              <div className="responses-grid">
                {questionResult.responses.slice(0, 20).map((response, index) => (
                  <div key={index} className="response-card">
                    <p className="response-text">{response.answer}</p>
                    <span className="response-author">- {response.name}</span>
                  </div>
                ))}
              </div>
              <p className="display-total-responses">{questionResult.responses.length} response{questionResult.responses.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
        );
      }
    }
  }

  // Final Leaderboard
  if (displayState === 'finished' && finalLeaderboard.length > 0) {
    return (
      <div className="display-with-sidebar final-display">
        {sidebar}
        <div className="ds-main ds-main-center">
          <h1 className="final-title">🎉 Final Results 🎉</h1>

          <div className="podium">
            {finalLeaderboard[1] && (
              <div className="podium-place second-place">
                <div className="podium-medal">🥈</div>
                <div className="podium-name">{finalLeaderboard[1].name}</div>
                <div className="podium-score">{finalLeaderboard[1].totalScore} pts</div>
                <div className="podium-stand stand-2">2</div>
              </div>
            )}
            {finalLeaderboard[0] && (
              <div className="podium-place first-place">
                <div className="podium-medal">🥇</div>
                <div className="podium-name">{finalLeaderboard[0].name}</div>
                <div className="podium-score">{finalLeaderboard[0].totalScore} pts</div>
                <div className="podium-stand stand-1">1</div>
              </div>
            )}
            {finalLeaderboard[2] && (
              <div className="podium-place third-place">
                <div className="podium-medal">🥉</div>
                <div className="podium-name">{finalLeaderboard[2].name}</div>
                <div className="podium-score">{finalLeaderboard[2].totalScore} pts</div>
                <div className="podium-stand stand-3">3</div>
              </div>
            )}
          </div>

          {finalLeaderboard.length > 3 && (
            <div className="other-players">
              <h3>Other Players</h3>
              <div className="other-players-list">
                {finalLeaderboard.slice(3).map((entry, index) => (
                  <div key={index} className="other-player-entry">
                    <span className="other-rank">{index + 4}</span>
                    <span className="other-name">{entry.name}</span>
                    <span className="other-score">{entry.totalScore} pts</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="game-complete-message">Thank you for playing!</div>
        </div>
      </div>
    );
  }

  return (
    <div className="display-with-sidebar">
      {sidebar}
      <div className="ds-main ds-main-center">
        <h1>Loading...</h1>
      </div>
    </div>
  );
}

export default AdminGameDisplay;
